import type { GeoUserProfileData } from '@/components/GeoTest/types';
import request, { requestFile } from './request';

import type { Rapper, Template, StateWellsResponse } from '@/components/WellLoggingOperations/types';
import type { AllRequestsBlockTypes } from '@/components/Reports/Sidebar/Filter/RequestsBlock/types';
import type { GeoData } from '@/store/geo-test/types';
import type { ProjectFund } from '@/store/project-fund';
import type { WellResearchResponse } from '@/components/Reports/types/well-research';
import type {
    InfoTypes,
    WellTypes,
} from '@/components/Reports/Sidebar/Form/forms/RequestsBlock/modals/shared/Checkboxes';
import type { SampleQualityTypes } from '@/components/Reports/Sidebar/Form/forms/RequestsBlock/modals/shared/SampleQuality';
import type { Interval } from '@/components/Reports/Sidebar/Form/forms/RequestsBlock/types';

type RappersByWells = Record<string, Rapper[]>;

interface WellsRappersResponse {
    packs: RappersByWells;
}

interface DeletedWellRapperResponse {
    message: string;
}

type CreatedRappersResponse = Rapper[];
type UpdatedRappersResponse = Rapper[];

interface Stratum {
    maxSiX: number;
    maxSiY: number;
    minSiX: number;
    minSiY: number;
    stratumId: number;
    title: string;
}

interface IndustrialObject {
    id: number;
    key: string;
    children?: IndustrialObject[];
    title: string;
}

interface ResponseStratums {
    stratums: Stratum[];
    objects: IndustrialObject[];
}

interface ResponseVersions {
    id: number;
    title: string;
}

type UserSettings = 'userSettings';

interface UserSaveSettings {
    key: UserSettings;
    data: Record<string, any>;
    // Для того, чтобы обновить значения только переданных полей, нужно дополнительно еще добавить параметр update: true
    update?: boolean;
}

interface WellsInfo {
    well_ids: number[] | { well_id: number };
}

interface ResponseWellsInfo {
    data: Array<{
        id: number;
        fieldId: number;
        fieldTitle: string;
        ventureTitle: string;
        areaTitle: string;
        iconId: number;
        status: string;
        date: string; //"2017-09-01T02:00:00"
        devObjects: string;
    }>;
}

export type Fgi = {
    stratumVersionId: number;
    source: string;
    remark: string | null;
    projectDoc: string;
    priority: number;
    date: string;
    fieldId: number;
    user: string;
    versionDate: string;
    wellCountInVersion: number;
    wellCountInField: number;
    wells_id: number[];
};

export type Rigis = {
    stratumVersionId: number;
    description: string;
    date: string;
    fieldId: number;
    user: string;
    versionDate: string;
    isResidual: number;
    wells_id: number[];
};

type StartFrom = Partial<{
    orgId: number[];
    fieldId: number[];
    areaId: number[];
    ventureId: number[];
    licenseZoneId: number[];
    kupolId: number[];
    explAreaId: number[];
    wellsId: number[];
}>;

export type RequestIntervalType = {
    startDate: string;
    endDate: string;
    onDate: string;
};

export type BlockRequestQueryParams = Partial<
    RequestIntervalType & {
        newWellAccompaniment?: {
            intervalNewWells: number | null;
            beforeDrilling: number | null;
            afterDrilling: number | null;
        };
        wellTypes: WellTypes[];
        infoTypes: InfoTypes[];
        nonMatchingAltitudes: boolean;
        breakdownOption: string;
        sampleQuality: SampleQualityTypes;
        includeShafts: boolean;
        researchesInterval: string[];
        conclusions: string[];
        allStratums: boolean;
        deepOilSamplesAallInfo: number;
        stratums: string[];
        stratumsDensity: Record<number, { oil?: number | null; water?: number | null }>;
        deposits: string[];
        flowMeteringResearch: string;
        deepOilSamplesTestQuality: string;
        wellConservationData: string;
        gdisReportGenerationResearches: string;
        shortForm: string;
        isShortForm: boolean;
        techMode: boolean;
        resultUnit: string;
        gisControlData: string;
        withPerforation: boolean;
        priority: string;
        queryViewConditions: string;
        perforationNotOpenStratum: boolean;
        queryViewConditionsWellBreakdown: string;
        filteredResearchesIntervals: Interval[];
        sampleTypes: number[];
    }
>;

export type QueryBlockRequest = {
    startFrom: StartFrom;
    queryName: AllRequestsBlockTypes;
    params: BlockRequestQueryParams;
};

export type QueryBlockRow = Record<string, string | number | null>;
export type QueryBlockResults = QueryBlockRow[];

export type TestingParam = {
    title: string;
    units: string | null;
    value: string;
};

export type TestingData = {
    id: number;
    roof: number;
    bottom: number;
    absRoof: number;
    absBottom: number;
    DOB: string;
    DOE: string;
    params: TestingParam[];
};

export type PerforationEntry = {
    type: 'PERFORATION';
    perfId: number;
    fieldTitle: string;
    stratumTitle: string;
    roof: number;
    bottom: number;
    absRoof: number;
    absBottom: number;
    mineNumber: string;
    wellNumber: string;
    perfTitle: string;
    gearTitle: string;
    dateTime: string;
    source: string | null;
    result: string;
    isIsolated: boolean;
    researchCode: string | null;
};

type IsolationEntry = {
    title: string;
    type: 'ISOLATION';
    fieldTitle: string;
    mineNumber: string;
    wellNumber: string;
    stratumTitle: string | null;
    roof: number;
    topDepthDrill: number;
    bottom: number;
    baseDepthDrill: number | null;
    isolationId: number;
    isolationType: string;
    purpose: string;
    drillDate: string | null;
    dateTime: string;
    absRoof: number;
    absBottom: number;
};

export type GraphData = {
    perforation: Record<number, PerforationEntry[]>;
    isolation: Record<number, IsolationEntry[]>;
};

export type TestingResponse = {
    testingByWell: Record<number, Record<number, TestingData>>;
};

export interface NewsEntry {
    entryID: string;
    entryTitle: string;
    [key: string]: unknown; // Extend as needed
}

export type IdTitle = {
    id: number;
    title: string;
};

export type ProjectFundWell = ProjectFundWellInfo & TitleId;

export type ProjectFundWellInfo = {
    FieldTitle: string;
    fieldId: number | null;
    explorationZoneId: number | null;
    explorationZone: string | null;
    mineNumber: string | null;
    mineId: number | null;
    wellNumber: string;
    oilFieldId: number;
    stratum: string | null;
    licenseZoneId: number | null;
    kupolId: number | null;
    kupol: string | null;
    areaId: number | null;
    area: string | null;
    wellboreValuesId: number;
    developmentIconId: number;
    stratumIntersectX: number;
    stratumIntersectY: number;
    bottomHoleX: number | null;
    bottomHoleY: number | null;
    outFallX: number | null;
    outFallY: number | null;
    drillDate: string | null;
    createdAt: string;
};

export type TitleId = {
    id: number;
};

export type CreateProjectFundRequest = {
    title: string;
    description: string;
    securityLevel: number;
    user: string;
    ventureId: number | null;
    fieldId: number | null;
    areaId: number | null;
    mineId: number | null;
    kupolId: number | null;
    wells: ProjectFundWellInfo[];
};

export type CreateProjectFundResponse = {
    projectFund: ProjectFund;
};

export type UpdateProjectFundRequest = Omit<CreateProjectFundRequest, 'wells'> & {
    id: number;
    deletedProjectFundWellIds: number[];
    changedProjectFundWells: ProjectFundWell[];
    addedProjectFundWells: ProjectFundWellInfo[];
};

export type ListProjectFundsParams = {
    ventureId?: number;
    fieldId?: number;
    areaId?: number;
    mineId?: number;
    kupolId?: number;
};

export type ListProjectFundsResponse = {
    securityLevel: IdTitle[];
    projectFund: ProjectFund[];
};

export type SetProjectFundColorRequest = {
    color: string;
};

export type SetProjectFundColorResponse = {
    projectFundId: number;
};

export type GetProjectFundDetailsResponse = {
    projectFundWells: ProjectFundWell[];
};

export type FieldsResponse = {
    fields: IdTitle[];
};

export type ExplorationZonesResponse = {
    explorationZones: IdTitle[];
};

export type ProjectFundInfoResponse = {
    stratums: Array<{ oilFieldId: number; stratumTitle: string }>;
    mines: Array<{ id: number; number: string }>;
    kupols: IdTitle[];
    areas: IdTitle[];
    licenseZones: IdTitle[];
    wellbores: IdTitle[];
    wellTypes: Array<IdTitle & { iconId: number }>;
};

export type ProjectFundUserInfoCreateRequest = {
    projectFundId: number;
    mine?: Array<{ value: string; fieldId: number }>;
    stratum?: Array<{ value: string; fieldId: number }>;
    area?: Array<{ value: string }>;
    expZone?: Array<{ value: string }>;
    kupol?: Array<{ value: string }>;
};

export type CustomOption = {
    id: number;
    fundId: number;
    value: string;
};

export type QueryTemplate = {
    queryName: string;
    templateName: string;
    isPrivate: boolean;
    params: string;
    id: number;
};

export type ValidateWellTableName = 'area' | 'expZone' | 'kupol' | 'well' | 'mine' | 'stratum';

export type ProjectFundUserInfoGetResponse = Record<ValidateWellTableName, CustomOption>;

export type ProjectFundUserInfoValidateRequest =
    | { table: 'area' | 'expZone' | 'kupol'; value: string }
    | { table: 'mine' | 'stratum'; value: string; fieldId: number }
    | { table: 'well'; value: string; fieldId: number; areaId: number };

interface Api {
    news: {
        downloadPdf: (entryUrl: string) => Promise<Blob>;
        downloadOriginal: (entryID: string) => Promise<Blob>;
        downloadMetadata: (entryID: string) => Promise<Blob>;
        downloadExcel: (entryID: string) => Promise<Blob>;
        downloadImage: (entryID: string) => Promise<Blob>;
    };
    user: {
        saveData: <T>(payload: UserSaveSettings) => Promise<T>;
        loadData: <T>(key: string) => Promise<T>;
    };
    carottage: {
        /*
            Rappers
        */
        fetchVersionWellPacks: (body: string) => Promise<WellsRappersResponse>;
        createWellsRappers: (body: string) => Promise<CreatedRappersResponse>;
        updateWellsRappers: (body: string) => Promise<UpdatedRappersResponse>;
        deleteWellRapper: (rapperId: string) => Promise<DeletedWellRapperResponse>;
        /*
            Templates
        */
        fetchTemplates: () => Promise<Template[]>;
        createTemplate: (body: string) => Promise<Template>;
        updateTemplate: (body: string) => Promise<Template>;
        deleteTemplate: (templateId: string) => Promise<Template>;
        /*
            State wells
        */
        fetchStateWells: () => Promise<StateWellsResponse>;
        // fetchStateWells: (body: string) => Promise<StateWellsResponse>;
        createStateWell: (body: string) => Promise<unknown>;
        fetchFgiList: (body: { wellsId: number[] }) => Promise<Fgi[]>;
        fetchRigisList: (body: { wellsId: number[] }) => Promise<Rigis[]>;
        fetchTesting: (body: { wellIds: number[] }) => Promise<TestingResponse>;
        fetchGraphData: (id: number, charts: string) => Promise<GraphData>;
    };
    reports: {
        fetchMerReports: (params: any) => Promise<any>;
        fetchMerReports_v2: (params: any) => Promise<any>;
        fetchAnyReports: (params: any) => Promise<any>;
        getMerExcel: (params: any) => Promise<any>;
        fetchTechInfoReport: (params: any) => Promise<any>;
        fetchWellResearchInfoReport: (params: any) => Promise<WellResearchResponse>;
        fetchTimeout: (params: any) => Promise<any>;
        fetchGdsProductionVersion: (params: any) => Promise<any>;
        downloadGdsArchive: (params: any) => Promise<any>;
        fetchGeoReport: (params: any) => Promise<any>;
        requestsBlock: (params: QueryBlockRequest) => Promise<QueryBlockResults>;
        fetchWellsResearchesIntervals: (body: StartFrom) => Promise<Interval[]>;
        fetchFluidTypes: () => Promise<Array<{ Title: string; ID: number }>>;
        requestsTemplate: {
            delete: (params: QueryTemplate) => Promise<QueryTemplate>;
            update: (params: QueryTemplate) => Promise<QueryTemplate>;
            create: (params: Omit<QueryTemplate, 'id'>) => Promise<QueryTemplate>;
            get: () => Promise<QueryTemplate[]>;
        };
    };
    map: {
        fetchFieldsByCompany: (params: any) => Promise<Record<string, string>>;
        fetchStratumsByFields: (params: any) => Promise<ResponseStratums>;
        fetchVersionsByFields: (params: any) => Promise<ResponseVersions[]>;
        projectFund: {
            create: (body: CreateProjectFundRequest) => Promise<CreateProjectFundResponse>;
            update: (body: UpdateProjectFundRequest) => Promise<CreateProjectFundResponse>;
            list: (params: ListProjectFundsParams) => Promise<ListProjectFundsResponse>;
            setColor: (projectFundId: number, body: SetProjectFundColorRequest) => Promise<SetProjectFundColorResponse>;
            details: (projectFundId: number) => Promise<GetProjectFundDetailsResponse>;
            delete: (projectFundId: number) => Promise<{ deletedProjectFundId: number }>;
            dropdownFields: (params: { ventureId?: number; fieldId?: number }) => Promise<FieldsResponse>;
            dropdownExplorationZones: (params: {
                ventureId?: number;
                fieldId?: number;
            }) => Promise<ExplorationZonesResponse>;
            info: (params: { explorationZoneId?: number; fieldId?: number }) => Promise<ProjectFundInfoResponse>;
            wellTypes: () => Promise<Array<IdTitle & { iconId: number }>>;
            createCustomOptions: (body: ProjectFundUserInfoCreateRequest) => Promise<{ save: boolean }>;
            getCustomOptions: (params: { projectFundId: number }) => Promise<ProjectFundUserInfoGetResponse>;
            validateFormField: (body: ProjectFundUserInfoValidateRequest) => Promise<{ isExist: boolean }>;
            allWells: () => Promise<GetProjectFundDetailsResponse>;
        };
    };
    tree: {
        fetchWellInfoByIds: (params: WellsInfo) => Promise<ResponseWellsInfo>;
    };
    geologicProfiles: {
        getGeologicProfiles: (body: {
            wellsId: number[];
            stratumVersionId: number;
            waterHorizon: number | null;
            gasHorizon: number | null;
            // isRigis: boolean; // Даниил удалил этот флаг на бэке
        }) => Promise<GeoData>;
        userProfiles: {
            add: (body: GeoUserProfileData) => Promise<GeoUserProfileData[]>;
            delete: (id: number) => Promise<{ deleted: string }>;
            change: (id: number, body: GeoUserProfileData) => Promise<GeoUserProfileData[]>;
            getAll: () => Promise<GeoUserProfileData[]>;
        };
    };
}

export const api: Api = {
    news: {
        downloadPdf: entryUrl => requestFile.get(`${entryUrl}`).blob(),
        downloadOriginal: entryID => request.get(`news/file/${entryID}`).blob(),
        downloadMetadata: entryID => request.get(`news/metadata/${entryID}`).blob(),
        downloadExcel: entryID => request.get(`news/file/${entryID}.xlsx`).blob(),
        downloadImage: entryID => request.get(`news/preview/${entryID}.png`).blob(),
    },
    user: {
        saveData: payload => request.post(`users/save/`, { body: JSON.stringify(payload) }).json(),
        loadData: key => request.get(`users/load/?key=${key}`).json(),
    },
    carottage: {
        /*
            Rappers
        */
        fetchVersionWellPacks: body => request.post(`logging/version-well-packs/`, { body }).json(),
        createWellsRappers: body => request.post(`logging/add-well-packs/`, { body }).json(),
        updateWellsRappers: body => request.post(`logging/change-well-packs/`, { body }).json(),
        deleteWellRapper: rapperId => request.get(`logging/delete-pack-data/?wellPackId=${rapperId}`).json(),
        /*
            Templates
        */
        fetchTemplates: () => request.get(`logging/logging-templates/`).json(),
        createTemplate: body => request.post(`logging/add-logging-template/`, { body }).json(),
        updateTemplate: body => request.post(`logging/change-logging-template/`, { body }).json(),
        deleteTemplate: templateId => request.get(`logging/delete-logging-template/?id=${templateId}`).json(),
        /*
            State wells
        */
        fetchStateWells: () => request.get(`logging/logging-states/`).json(),
        //  fetchProfilesData: body => request.post(`logging/profile-data/`, { body: JSON.stringify(body) }).json(),
        // fetchStateWells: body => request.post(`logging/profile-data/`, { body: JSON.stringify(body) }).json(),
        // createStateWell: body => request.post(`logging/add-logging-state/`, { body }).json(),
        //  fetchProfilesData: body => request.post(`logging/profile-data/`, { body: JSON.stringify(body) }).json(),
        createStateWell: body => request.post(`logging/profile/`, { body }).json(),
        fetchFgiList: body => request.post(`logging/get-manager-fgi-530/`, { body: JSON.stringify(body) }).json(),
        fetchRigisList: (body = { wellsId: [] }) =>
            request.post(`logging/get-manager-rigis/`, { body: JSON.stringify(body) }).json(),
        fetchTesting: body => request.post(`logging/well-testing/`, { body: JSON.stringify(body) }).json(),
        fetchGraphData: (id, charts) =>
            request.get(`logging/graph-data/?params={"well": ${id}, "charts": ${charts}}`).json(),
    },
    reports: {
        fetchMerReports: params => request.post('reports/get-mer/', { body: JSON.stringify(params) }),
        fetchMerReports_v2: params => request.post('reports/get-mer-v2/', { body: JSON.stringify(params) }),
        fetchAnyReports: params => request.post('reports/get-any-report/', { body: JSON.stringify(params) }),
        getMerExcel: params => request.post('reports/get-mer-excel/', { body: JSON.stringify(params) }),
        fetchTechInfoReport: params => request.post('reports/well-status/', { body: JSON.stringify(params) }),
        fetchWellResearchInfoReport: params =>
            request.post('reports/well-research/', { body: JSON.stringify(params) }).json(),
        fetchFluidTypes: () => request.get('reports/fluid-types/').json(),
        fetchTimeout: () => request.get(`test/timeout/?params={"timeout":600}`).json(),
        fetchGdsProductionVersion: params =>
            request.post('reports/gds-production-version/', { body: JSON.stringify(params) }),
        downloadGdsArchive: params => request.post('reports/gds/', { body: JSON.stringify(params) }),
        fetchGeoReport: params => request.post('reports/geo/', { body: JSON.stringify(params) }),
        fetchWellsResearchesIntervals: body =>
            request.post(`reports/well-research-meta/`, { body: JSON.stringify(body) }).json(),
        requestsBlock: params => request.post('reports/queries-block/', { body: JSON.stringify(params) }).json(),
        requestsTemplate: {
            create: params => request.post('reports/queries-block-templates/', { body: JSON.stringify(params) }).json(),
            get: () => request.get('reports/queries-block-templates/').json(),
            update: params => request.put('reports/queries-block-templates/', { body: JSON.stringify(params) }).json(),
            delete: params =>
                request.delete('reports/queries-block-templates/', { body: JSON.stringify(params) }).json(),
        },
    },
    geologicProfiles: {
        getGeologicProfiles: body => request.post('logging/geologic-profile/', { body: JSON.stringify(body) }).json(),
        userProfiles: {
            add: body => request.post('logging/users-geologic-profile/', { body: JSON.stringify(body) }).json(),
            change: (id, body) =>
                request.put(`logging/users-geologic-profile/?id=${id}`, { body: JSON.stringify(body) }).json(),
            delete: id => request.delete(`logging/users-geologic-profile/?id=${id}`).json(),
            getAll: () => request.get('logging/users-geologic-profile/').json(),
        },
    },
    map: {
        // Получение месторождений по ТПП
        fetchFieldsByCompany: params => request.post('map/fields-by-node/', { body: JSON.stringify(params) }).json(),
        // Получение пластов
        fetchStratumsByFields: params => request.post('map/stratums-by-node/', { body: JSON.stringify(params) }).json(),
        //Получение версий
        fetchVersionsByFields: params =>
            request.post('map/production-version/', { body: JSON.stringify(params) }).json(),
        projectFund: {
            create: body => request.post(`map/project-fund/`, { body: JSON.stringify(body) }).json(),
            update: body => request.put(`map/project-fund/`, { body: JSON.stringify(body) }).json(),
            list: searchParams => request.get(`map/project-fund/`, { searchParams }).json(),
            setColor: (projectFundId, body) =>
                request.patch(`map/project-fund/${projectFundId}/`, { body: JSON.stringify(body) }).json(),
            details: projectFundId => request.get(`map/project-fund/${projectFundId}/`).json(),
            delete: projectFundId => request.delete(`map/project-fund/${projectFundId}/`).json(),
            dropdownFields: searchParams => request.get(`map/project-fund/fields/`, { searchParams }).json(),
            dropdownExplorationZones: searchParams =>
                request.get(`map/project-fund/exploration-zones/`, { searchParams }).json(),
            info: searchParams => request.get(`map/project-fund/info/`, { searchParams }).json(),
            createCustomOptions: body =>
                request.post(`map/project-fund/user-info/`, { body: JSON.stringify(body) }).json(),
            getCustomOptions: searchParams => request.get(`map/project-fund/user-info/`, { searchParams }).json(),
            validateFormField: body =>
                request.post(`map/project-fund/user-info-validate/`, { body: JSON.stringify(body) }).json(),
            allWells: () => request.get(`map/project-fund/wells/`).json(),
            wellTypes: () => request.get(`map/project-fund/well-types/`).json(),
        },
    },
    tree: {
        // Получение информации по месторождениям по их id
        fetchWellInfoByIds: params => request.post('tree-info/well-info/', { body: JSON.stringify(params) }).json(),
    },
};
